/* Initiative #02 — audit-client.js
 * Consumes SSE from audit-api.weltpixel.com and drives the 6-state UI.
 * Redesign pass (Session 49, 2026-04-24): render queue with pacing so
 * checks animate sequentially even on cached scans; category grouping
 * for scanning + results; FAQ accordion; URL input prefix handling.
 */
(() => {
  "use strict";

  const API_BASE = "https://audit-api.weltpixel.com";
  const TURNSTILE_SITE_KEY = window.__AUDIT_TURNSTILE_SITE_KEY || "";
  const MIN_RENDER_GAP_MS = 320;
  const RUNNING_DWELL_MS = 450;

  const hero = document.getElementById("audit-hero");
  if (!hero) return;

  // ── Dev bypass handling ──
  const SS_KEY = "audit_dev_bypass";
  const urlParams = new URLSearchParams(window.location.search);
  const urlBypass = urlParams.get("dev_bypass");
  if (urlBypass) {
    try { sessionStorage.setItem(SS_KEY, urlBypass); } catch {}
    urlParams.delete("dev_bypass");
    const qs = urlParams.toString();
    const newUrl = window.location.pathname + (qs ? "?" + qs : "") + window.location.hash;
    try { history.replaceState(null, "", newUrl); } catch {}
  }
  const devBypassToken = (() => {
    try { return sessionStorage.getItem(SS_KEY) || ""; } catch { return ""; }
  })();
  const isDev = devBypassToken.length > 0;
  if (isDev) {
    const badge = document.createElement("div");
    badge.textContent = "DEV BYPASS";
    badge.setAttribute("style",
      "position:fixed;top:10px;right:10px;background:#e63e6d;color:#fff;" +
      "padding:4px 10px;border-radius:4px;font:600 11px/1.4 system-ui;" +
      "letter-spacing:.05em;z-index:99999;cursor:pointer;opacity:.85;");
    badge.title = "Click to exit dev bypass mode";
    badge.addEventListener("click", () => {
      try { sessionStorage.removeItem(SS_KEY); } catch {}
      window.location.reload();
    });
    document.body.appendChild(badge);
  }

  // ── DOM lookups ──
  const states = Object.fromEntries(
    [...hero.querySelectorAll("[data-audit-state]")].map(el => [el.dataset.auditState, el])
  );
  const form = document.getElementById("audit-form");
  const domainEls = [...hero.querySelectorAll("[data-audit-domain]")];
  const checkList = hero.querySelector("[data-audit-check-list]");
  const pillsEl = hero.querySelector("[data-audit-pills]");
  const findingGroupsEl = hero.querySelector("[data-audit-finding-groups]");
  const installLink = hero.querySelector("[data-audit-install-link]");
  const subscribeForm = document.getElementById("audit-subscribe-form");
  const subscribeStatusEl = hero.querySelector("[data-audit-subscribe-status]");
  const subscribeBoxEl = hero.querySelector("[data-audit-subscribe-box]");
  const subscribedBoxEl = hero.querySelector("[data-audit-subscribed-box]");
  const subscribedDomainEl = hero.querySelector("[data-audit-subscribed-domain]");
  const rateSubscribeForm = document.getElementById("audit-rate-limit-subscribe-form");
  // Swap the subscribe form on the results page for a "you're already
  // subscribed" confirmation. Called when the user arrives via the
  // subscribe-reward path from rate-limited OR completes the results-page
  // subscribe form. Session-scoped — a new scan resets this.
  let subscribedPreset = false;
  function showSubscribedConfirmation(domainText) {
    subscribedPreset = true;
    if (subscribedDomainEl && domainText) subscribedDomainEl.textContent = domainText;
    if (subscribeBoxEl) subscribeBoxEl.hidden = true;
    if (subscribedBoxEl) subscribedBoxEl.hidden = false;
  }
  const errorMsgEl = hero.querySelector("[data-audit-error-message]");
  const errorDetailEl = hero.querySelector("[data-audit-error-detail]");
  const retryCountdownEl = hero.querySelector("[data-audit-retry-countdown]");
  const progressFillEl = hero.querySelector("[data-audit-progress-fill]");
  const progressCountEl = hero.querySelector("[data-audit-progress-count]");
  const scanSubtitleEl = hero.querySelector("[data-audit-scan-subtitle]");
  const urlInput = document.getElementById("audit-url");

  let lastScan = { url: null, depth: null, scanId: null, summary: null, failedKeys: [] };
  let currentCheckItems = new Map();
  let currentCheckDetails = new Map();
  let checkOrder = [];

  // ── Constants: labels + categories ──
  const PRETTY_LABELS = {
    "platform-detect": "Shopify platform check",
    "meta-pixel": "Meta Pixel",
    "tiktok-pixel": "TikTok Pixel",
    "ga4-pixel": "Google Analytics 4 tag",
    "google-ads": "Google Ads conversion tag",
    "consent-mode": "Consent Mode V2 framework",
    "consent-mode-correctness": "Consent Mode V2 correctness",
    "ga4-events": "Google Analytics 4 e-commerce events",
    "meta-capi": "Meta CAPI (server-side)",
    "tiktok-events-api": "TikTok Events API (server-side)",
    "duplicate-pixels": "Duplicate pixels",
    "shop-pay-gap": "Shop Pay checkout tracking",
    "server-gtm": "Server-side GTM",
    "purchase-event": "Purchase event configuration",
  };

  // Category grouping — display order matches site flow
  const CATEGORIES = [
    { id: "platform",      title: "Platform",                 keys: ["platform-detect"] },
    { id: "browser",       title: "Browser pixels",           keys: ["meta-pixel", "tiktok-pixel", "ga4-pixel", "ga4-events", "google-ads"] },
    { id: "server",        title: "Server-side destinations", keys: ["meta-capi", "tiktok-events-api", "server-gtm"] },
    { id: "compliance",    title: "Compliance & integrity",   keys: ["consent-mode", "consent-mode-correctness", "duplicate-pixels", "purchase-event", "shop-pay-gap"] },
  ];
  const KEY_TO_CATEGORY = Object.fromEntries(
    CATEGORIES.flatMap(c => c.keys.map(k => [k, c.id]))
  );

  const DEPTH_KEYS = {
    quick:    ["platform-detect", "meta-pixel", "tiktok-pixel", "ga4-pixel", "google-ads", "consent-mode"],
    standard: ["platform-detect", "meta-pixel", "tiktok-pixel", "ga4-pixel", "google-ads", "consent-mode", "ga4-events", "meta-capi", "tiktok-events-api", "duplicate-pixels", "shop-pay-gap"],
    thorough: ["platform-detect", "meta-pixel", "tiktok-pixel", "ga4-pixel", "google-ads", "consent-mode", "ga4-events", "meta-capi", "tiktok-events-api", "duplicate-pixels", "shop-pay-gap", "consent-mode-correctness", "server-gtm", "purchase-event"],
  };

  // ── SVG icon builders ──
  // createElementNS builds the SVG directly in the current document, so
  // nothing gets stuck in a detached DOMParser document (which silently
  // fails to render in some browsers). No HTML string parsing involved,
  // so no XSS concerns.
  const SVG_NS = "http://www.w3.org/2000/svg";
  function makeStatusIcon(status) {
    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("viewBox", "0 0 20 20");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2.2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");
    const addPath = (d) => {
      const p = document.createElementNS(SVG_NS, "path");
      p.setAttribute("d", d);
      svg.appendChild(p);
    };
    if (status === "pass") addPath("M4 10l4 4 8-8");
    else if (status === "fail") { addPath("M5 5l10 10"); addPath("M15 5l-10 10"); }
    else if (status === "warn") { addPath("M10 4l8 13H2L10 4z"); addPath("M10 9v4"); addPath("M10 15v.5"); }
    else {
      // Neutral icon for skipped / unknown statuses: a dash inside a muted circle.
      addPath("M5 10h10");
    }
    return svg;
  }
  function makeChevronIcon() {
    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("viewBox", "0 0 20 20");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");
    const p = document.createElementNS(SVG_NS, "path");
    p.setAttribute("d", "M5 8l5 5 5-5");
    svg.appendChild(p);
    return svg;
  }

  function setIconFromStatus(iconEl, status) {
    iconEl.textContent = "";
    if (status === "running" || status === "pending") return;
    // makeStatusIcon always returns an icon (neutral dash for unknown).
    const icon = makeStatusIcon(status);
    if (icon) iconEl.appendChild(icon);
  }
  function statusChipText(s) {
    return s === "pass" ? "Pass" :
           s === "fail" ? "Fail" :
           s === "warn" ? "Warn" :
           s === "skip" || s === "unknown" ? "N/A" : "";
  }

  function go(state) {
    hero.dataset.state = state;
    for (const [k, el] of Object.entries(states)) el.hidden = (k !== state);
  }
  function setDomain(d) { domainEls.forEach(el => (el.textContent = d)); }

  function ga4(name, params) {
    if (typeof window.gtag === "function") window.gtag("event", name, params);
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...params });
  }

  // ── URL input https:// prefix handling ──
  // Keep the protocol visible but non-editable; strip any pasted
  // protocol or leading `www.` so the submit value is always a bare domain.
  if (urlInput) {
    const stripProtocol = (s) => s.replace(/^\s*(?:https?:)?\/\/(?:www\.)?/i, "").trim();
    urlInput.addEventListener("paste", (e) => {
      const txt = (e.clipboardData || window.clipboardData)?.getData("text") ?? "";
      if (!txt) return;
      e.preventDefault();
      const cleaned = stripProtocol(txt);
      const start = urlInput.selectionStart ?? urlInput.value.length;
      const end = urlInput.selectionEnd ?? urlInput.value.length;
      urlInput.value = urlInput.value.slice(0, start) + cleaned + urlInput.value.slice(end);
      urlInput.setSelectionRange(start + cleaned.length, start + cleaned.length);
    });
    urlInput.addEventListener("blur", () => {
      const cleaned = stripProtocol(urlInput.value);
      if (cleaned !== urlInput.value) urlInput.value = cleaned;
    });
  }

  // ── Turnstile loader ──
  (function loadTurnstile() {
    if (isDev) return; // dev bypass skips Turnstile entirely
    if (window.turnstile) return renderTurnstile();
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__turnstileReady";
    s.async = true;
    window.__turnstileReady = renderTurnstile;
    document.head.appendChild(s);
  })();

  let turnstileWidgetId = null;
  let turnstilePending = null;
  function renderTurnstile() {
    if (!window.turnstile) return;
    const container = document.getElementById("audit-turnstile");
    if (!container || !TURNSTILE_SITE_KEY) return;
    turnstileWidgetId = window.turnstile.render(container, {
      sitekey: TURNSTILE_SITE_KEY,
      size: "invisible",
      execution: "execute",
      callback: (token) => {
        if (turnstilePending) { turnstilePending.resolve(token); turnstilePending = null; }
      },
      "error-callback": () => {
        if (turnstilePending) { turnstilePending.resolve(""); turnstilePending = null; }
      },
      "timeout-callback": () => {
        if (turnstilePending) { turnstilePending.resolve(""); turnstilePending = null; }
      },
    });
  }

  function acquireTurnstileToken() {
    return new Promise((resolve) => {
      if (!window.turnstile || turnstileWidgetId === null) return resolve("");
      try { window.turnstile.reset(turnstileWidgetId); } catch {}
      turnstilePending = { resolve };
      try { window.turnstile.execute(turnstileWidgetId); } catch { resolve(""); turnstilePending = null; }
    });
  }

  // ── Form submit ──
  form?.addEventListener("submit", async (e) => {
    e.preventDefault();
    const fd = new FormData(form);
    const rawDomain = String(fd.get("url") || "").trim().replace(/^\s*(?:https?:)?\/\/(?:www\.)?/i, "");
    if (!rawDomain) return;
    const url = "https://" + rawDomain;
    const depth = String(fd.get("depth") || "standard");

    let token = "";
    if (!isDev) {
      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;
      token = await acquireTurnstileToken();
      if (submitBtn) submitBtn.disabled = false;
      if (!token) {
        showError("CAPTCHA check failed", "We couldn't verify your browser. Reload the page and try again.");
        return;
      }
    }

    lastScan = { url, depth, scanId: null, summary: null, failedKeys: [] };
    setDomain(rawDomain);
    // Render the check list BEFORE transitioning so the user sees all the
    // cards laid out with a spinner on the first one — no empty gap while
    // the server boots, no layout shift when the first event lands.
    initCheckList({ depth });
    go("scanning");

    ga4("audit_started", { depth, url_hash: await sha256Hex(url), turnstile_ok: !isDev });
    await runScan({ url, depth, turnstile_token: token });
  });

  async function runScan(body) {
    try { await fetch(`${API_BASE}/health`, { method: "GET", cache: "no-cache" }); } catch {}

    const qsObj = { url: body.url, depth: body.depth };
    if (body.turnstile_token) qsObj.turnstile_token = body.turnstile_token;
    if (isDev && devBypassToken) qsObj.dev_bypass = devBypassToken;
    if (body.scan_bypass) qsObj.scan_bypass = body.scan_bypass;
    if (body.force) qsObj.force = "1";
    const qs = new URLSearchParams(qsObj).toString();
    const es = new EventSource(`${API_BASE}/scan?${qs}`);

    let finished = false;
    const finishAndClose = () => { finished = true; try { es.close(); } catch {} };

    es.addEventListener("start", (e) => {
      // The check list was already pre-populated on form submit, so we
      // only re-init if the server reports a different depth than we sent.
      try {
        const data = JSON.parse(e.data);
        if (data && data.depth && currentCheckItems.size === 0) initCheckList(data);
      } catch {}
    });
    es.addEventListener("platform_check", (e) => {
      let data; try { data = JSON.parse(e.data); } catch { return; }
      if (data && data.is_shopify === false) {
        ga4("audit_declined", { reason: "non_shopify" });
        go("decline");
        finishAndClose();
        return;
      }
      enqueueCheck({
        key: "platform-detect",
        label: "Shopify platform check",
        status: "pass",
        detail: "Confirmed Shopify storefront.",
      });
    });
    es.addEventListener("check_result", (e) => { try { enqueueCheck(JSON.parse(e.data)); } catch {} });
    es.addEventListener("end", (e) => {
      try { scheduleFinalize(JSON.parse(e.data)); } catch {}
      finishAndClose();
    });
    es.addEventListener("error", (e) => {
      const dataStr = e && typeof e.data === "string" ? e.data : "";
      if (dataStr) {
        let data = {};
        try { data = JSON.parse(dataStr); } catch {}
        const code = data?.code;
        if (code === "rate_limited") {
          ga4("audit_rate_limited", { tier: "hour" });
          go("rate-limited");
          const nextAvail = data.next_available_at || (Date.now() + 60_000);
          const tick = () => {
            const ms = Math.max(0, nextAvail - Date.now());
            retryCountdownEl.textContent = `${Math.floor(ms / 60000)} min ${Math.floor((ms % 60000) / 1000)} sec`;
            if (ms > 0) setTimeout(tick, 1000);
          };
          tick();
        } else if (code === "turnstile_failed") {
          showError("CAPTCHA verification failed", "Your Turnstile token expired or was already used. Reload the page and solve the CAPTCHA again.");
        } else if (code === "invalid_url" || code === "invalid_body") {
          showError("Invalid request", "Check that the URL is well-formed.");
        } else if (code === "unsafe_target") {
          showError("Invalid request", `Can't scan that URL (${data.reason || "unsafe"}).`);
        } else if (code === "blocked_domain") {
          showError("We can't audit this one", `The audit tool can't scan ${data.domain || "this domain"} — try a different Shopify store.`);
        } else {
          showError("Scan error", String(data?.message ?? code ?? ""));
        }
        finishAndClose();
        return;
      }
      if (finished) return;
      if (es.readyState === EventSource.CLOSED) {
        showError("Network error", "Connection closed before the scan finished. Try again in a minute.");
        finishAndClose();
      }
    });
  }

  // ── Render queue ──
  // Every incoming check_result waits its turn with a minimum gap so the
  // UI animates through each check even when the server flushes all of
  // them in the same tick (cache hit, fast scan).
  const pendingRenders = [];
  let finalizeBuffer = null;
  let queueRunning = false;
  let lastRenderAt = 0;
  let progressNumerator = 0;

  function enqueueCheck(result) {
    pendingRenders.push(result);
    pumpQueue();
  }
  function scheduleFinalize(endData) {
    finalizeBuffer = endData;
    pumpQueue();
  }
  async function pumpQueue() {
    if (queueRunning) return;
    queueRunning = true;
    while (pendingRenders.length) {
      const now = performance.now();
      const wait = Math.max(0, lastRenderAt + MIN_RENDER_GAP_MS - now);
      if (wait) await sleep(wait);
      const next = pendingRenders.shift();
      await renderNext(next);
      lastRenderAt = performance.now();
    }
    if (finalizeBuffer) {
      await sleep(Math.max(0, lastRenderAt + MIN_RENDER_GAP_MS - performance.now()));
      const data = finalizeBuffer; finalizeBuffer = null;
      await finalize(data);
    }
    queueRunning = false;
  }

  async function renderNext(result) {
    const li = currentCheckItems.get(result.key);
    if (!li) return;
    // Flip to "running" briefly so the user sees the spinner even on cache
    li.dataset.status = "running";
    const iconEl = li.querySelector(".audit-check-icon");
    if (iconEl) { iconEl.textContent = ""; }
    await sleep(RUNNING_DWELL_MS);
    li.dataset.status = result.status;
    if (iconEl) setIconFromStatus(iconEl, result.status);
    const labelEl = li.querySelector(".audit-check-label");
    if (labelEl) labelEl.textContent = result.label || PRETTY_LABELS[result.key] || result.key;
    // Stash detail text in a Map — the check list doesn't render it
    // (space-constrained), but the results page reads it to populate each
    // finding card's paragraph copy.
    currentCheckDetails.set(result.key, result.detail || "");
    const chipEl = li.querySelector(".audit-check-status-chip");
    if (chipEl) chipEl.textContent = statusChipText(result.status);
    if (result.status === "fail" || result.status === "warn") lastScan.failedKeys.push(result.key);
    progressNumerator++;
    updateProgress();
    // Advance the spinner to the next pending check so there's always
    // visible activity between events instead of a silent gap.
    const idx = checkOrder.indexOf(result.key);
    if (idx >= 0 && idx + 1 < checkOrder.length) {
      const nextItem = currentCheckItems.get(checkOrder[idx + 1]);
      if (nextItem && nextItem.dataset.status === "pending") {
        nextItem.dataset.status = "running";
      }
    }
  }

  function updateProgress() {
    if (!progressCountEl || !progressFillEl) return;
    const total = checkOrder.length || 1;
    const pct = Math.min(100, Math.round((progressNumerator / total) * 100));
    progressFillEl.style.width = pct + "%";
    progressCountEl.textContent = `${progressNumerator} / ${total}`;
  }

  // ── Check list init (grouped by category) ──
  function initCheckList(startData) {
    if (!checkList) return;
    checkList.textContent = "";
    currentCheckItems = new Map();
    currentCheckDetails = new Map();
    checkOrder = DEPTH_KEYS[startData.depth] || [];
    progressNumerator = 0;
    updateProgress();
    // Reset the subscribe/subscribed swap so a fresh scan gets a fresh form
    // (unless pre-armed earlier in the subscribe-reward flow).
    if (!subscribedPreset) {
      if (subscribeBoxEl) subscribeBoxEl.hidden = false;
      if (subscribedBoxEl) subscribedBoxEl.hidden = true;
    }
    // Update the scanning subtitle to match the selected depth.
    if (scanSubtitleEl) {
      const label = startData.depth === "quick" ? "Quick" : startData.depth === "thorough" ? "Thorough" : "Standard";
      const estimate = startData.depth === "quick" ? "~25s" : startData.depth === "thorough" ? "~75s" : "~45s";
      scanSubtitleEl.textContent = `${label} scan · ${checkOrder.length} checks · ${estimate}`;
    }

    // Build per-category blocks in the display order; skip categories with no keys in this depth
    for (const cat of CATEGORIES) {
      const keysInCat = cat.keys.filter(k => checkOrder.includes(k));
      if (!keysInCat.length) continue;
      const groupEl = document.createElement("li");
      groupEl.className = "audit-check-group";

      const titleEl = document.createElement("h3");
      titleEl.className = "audit-check-group-title";
      titleEl.textContent = cat.title;
      groupEl.appendChild(titleEl);

      for (const key of keysInCat) {
        const item = document.createElement("div");
        item.className = "audit-check-item";
        item.dataset.status = "pending";
        item.dataset.key = key;

        const icon = document.createElement("span");
        icon.className = "audit-check-icon";
        icon.setAttribute("aria-hidden", "true");

        const label = document.createElement("span");
        label.className = "audit-check-label";
        label.textContent = PRETTY_LABELS[key] || key;

        const chip = document.createElement("span");
        chip.className = "audit-check-status-chip";
        chip.textContent = "";

        item.appendChild(icon);
        item.appendChild(label);
        item.appendChild(chip);
        groupEl.appendChild(item);
        currentCheckItems.set(key, item);
      }
      checkList.appendChild(groupEl);
    }

    // Kick off the first check as "running" immediately so the user sees
    // a spinner while the server/Playwright is warming up. When the real
    // event arrives, renderNext flips it to the final status.
    if (checkOrder.length > 0) {
      const firstItem = currentCheckItems.get(checkOrder[0]);
      if (firstItem) firstItem.dataset.status = "running";
    }
  }

  // ── Finalize (render grouped results) ──
  async function finalize(endData) {
    lastScan.scanId = endData.scan_id;
    lastScan.summary = endData.summary;

    renderPills(endData.summary);
    renderFindingGroups();

    go("results");
    ga4("audit_completed", {
      depth: lastScan.depth,
      pass_count: endData.summary.pass,
      fail_count: endData.summary.fail,
      warn_count: endData.summary.warn,
    });
    // Wire the install CTA with UTM + failed-keys context
    const issueCode = lastScan.failedKeys.slice(0, 4).join("-") || "all-ok";
    const utmUrl = new URL("https://apps.shopify.com/weltpixel-conversion-tracking");
    utmUrl.searchParams.set("utm_source", "audit");
    utmUrl.searchParams.set("utm_medium", "tool");
    utmUrl.searchParams.set("utm_campaign", "audit-results");
    utmUrl.searchParams.set("utm_content", issueCode);
    if (installLink) {
      installLink.href = utmUrl.toString();
      installLink.addEventListener("click",
        () => ga4("install_link_click", { url_hash: domainOf(lastScan.url), ...endData.summary }),
        { once: true });
    }
  }

  function renderPills(summary) {
    if (!pillsEl) return;
    pillsEl.textContent = "";
    const pills = [
      { cls: "audit-pill-pass", n: summary.pass, label: "passing", status: "pass" },
      { cls: "audit-pill-fail", n: summary.fail, label: "failing", status: "fail" },
      { cls: "audit-pill-warn", n: summary.warn, label: "warnings", status: "warn" },
    ];
    for (const p of pills) {
      if (!p.n) continue;
      const span = document.createElement("span");
      span.className = "audit-pill " + p.cls;
      const icon = makeStatusIcon(p.status);
      if (icon) span.appendChild(icon);
      const txt = document.createElement("span");
      txt.textContent = `${p.n} ${p.label}`;
      span.appendChild(txt);
      pillsEl.appendChild(span);
    }
  }

  function renderFindingGroups() {
    if (!findingGroupsEl) return;
    findingGroupsEl.textContent = "";

    // Collect findings per category in order. Detail text lives in
    // currentCheckDetails (Map) — the check list doesn't render it.
    const rows = [...currentCheckItems.entries()].map(([key, li]) => ({
      key,
      status: li.dataset.status,
      label: li.querySelector(".audit-check-label")?.textContent || PRETTY_LABELS[key] || key,
      detail: currentCheckDetails.get(key) || "",
    }));

    for (const cat of CATEGORIES) {
      const catRows = rows.filter(r => (KEY_TO_CATEGORY[r.key] === cat.id));
      if (!catRows.length) continue;
      // Severity-sort within category
      catRows.sort((a, b) => rankStatus(a.status) - rankStatus(b.status));

      const counts = {
        fail: catRows.filter(r => r.status === "fail").length,
        warn: catRows.filter(r => r.status === "warn").length,
        pass: catRows.filter(r => r.status === "pass").length,
      };

      const group = document.createElement("div");
      group.className = "audit-finding-group";
      // Expand by default if the group has any fail/warn; otherwise collapse
      const shouldExpand = counts.fail > 0 || counts.warn > 0;
      group.dataset.expanded = shouldExpand ? "true" : "false";

      const header = document.createElement("button");
      header.type = "button";
      header.className = "audit-finding-group-header";
      header.setAttribute("aria-expanded", String(shouldExpand));

      const title = document.createElement("div");
      title.className = "audit-finding-group-title";
      const titleText = document.createElement("span");
      titleText.textContent = cat.title;
      title.appendChild(titleText);

      const countsEl = document.createElement("div");
      countsEl.className = "audit-finding-group-counts";
      if (counts.fail) {
        const c = document.createElement("span");
        c.className = "audit-finding-group-count audit-finding-group-count-fail";
        c.textContent = `${counts.fail} failing`;
        countsEl.appendChild(c);
      }
      if (counts.warn) {
        const c = document.createElement("span");
        c.className = "audit-finding-group-count audit-finding-group-count-warn";
        c.textContent = `${counts.warn} warning${counts.warn === 1 ? "" : "s"}`;
        countsEl.appendChild(c);
      }
      if (counts.pass) {
        const c = document.createElement("span");
        c.className = "audit-finding-group-count audit-finding-group-count-pass";
        c.textContent = `${counts.pass} passing`;
        countsEl.appendChild(c);
      }

      const headerLeft = document.createElement("div");
      headerLeft.style.cssText = "display:flex;flex-direction:column;gap:6px;align-items:flex-start;min-width:0;flex:1;";
      headerLeft.appendChild(title);
      headerLeft.appendChild(countsEl);

      const chev = makeChevronIcon();
      chev.classList.add("audit-finding-group-chevron");

      header.appendChild(headerLeft);
      header.appendChild(chev);

      const body = document.createElement("div");
      body.className = "audit-finding-group-body";

      for (const r of catRows) {
        const f = document.createElement("div");
        f.className = "audit-finding";
        f.dataset.status = r.status;

        const iconWrap = document.createElement("div");
        iconWrap.className = "audit-finding-icon";
        const icon = makeStatusIcon(r.status);
        if (icon) iconWrap.appendChild(icon);

        const bodyEl = document.createElement("div");
        bodyEl.className = "audit-finding-body";
        const titleRow = document.createElement("div");
        titleRow.className = "audit-finding-title";
        const h = document.createElement("h4");
        h.textContent = r.label;
        titleRow.appendChild(h);
        const chip = document.createElement("span");
        chip.className = "audit-check-status-chip";
        chip.textContent = statusChipText(r.status);
        titleRow.appendChild(chip);
        const p = document.createElement("p");
        p.textContent = r.detail;

        bodyEl.appendChild(titleRow);
        bodyEl.appendChild(p);
        f.appendChild(iconWrap);
        f.appendChild(bodyEl);
        body.appendChild(f);
      }

      group.appendChild(header);
      group.appendChild(body);
      findingGroupsEl.appendChild(group);

      // Set initial max-height after layout settles
      requestAnimationFrame(() => {
        body.style.maxHeight = shouldExpand ? (body.scrollHeight + "px") : "0px";
      });

      header.addEventListener("click", () => {
        const nowExpanded = group.dataset.expanded !== "true";
        group.dataset.expanded = nowExpanded ? "true" : "false";
        header.setAttribute("aria-expanded", String(nowExpanded));
        body.style.maxHeight = nowExpanded ? (body.scrollHeight + "px") : "0px";
      });
    }
  }

  // ── Subscribe forms ──
  function authHeaders() {
    const h = { "content-type": "application/json" };
    if (isDev && devBypassToken) h["x-audit-dev-bypass"] = devBypassToken;
    return h;
  }

  subscribeForm?.addEventListener("submit", async (e) => {
    e.preventDefault();
    const email = String(new FormData(subscribeForm).get("email") || "").trim();
    if (!email || !lastScan.url) return;
    const btn = subscribeForm.querySelector('button[type="submit"]');
    const emailInput = subscribeForm.querySelector('input[name="email"]');
    const originalLabel = btn ? btn.innerHTML : "";
    // Lock the button's current width so it doesn't shrink/grow as the
    // label swaps between "Get monthly reports" → "Subscribing" → "Subscribed ✓".
    if (btn) {
      const w = btn.getBoundingClientRect().width;
      if (w > 0) btn.style.minWidth = w + "px";
      btn.disabled = true;
      btn.classList.add("audit-button--loading");
      btn.textContent = "Subscribing";
    }
    if (emailInput) emailInput.disabled = true;
    if (subscribeStatusEl) subscribeStatusEl.textContent = "";
    // Only send a positive scanId — otherwise zod on the server rejects it.
    const sourceScanId = (typeof lastScan.scanId === "number" && lastScan.scanId > 0)
      ? lastScan.scanId
      : undefined;
    try {
      const res = await fetch(`${API_BASE}/subscribe`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ url: lastScan.url, email, depth: lastScan.depth, source_scan_id: sourceScanId }),
      });
      if (btn) { btn.classList.remove("audit-button--loading"); }
      if (res.ok) {
        // Button itself communicates success — no redundant copy below.
        if (btn) {
          btn.disabled = true;
          btn.classList.add("audit-button--success");
          btn.textContent = "Subscribed ✓";
        }
        if (emailInput) emailInput.disabled = true;
        ga4("subscribe_success", { depth: lastScan.depth });
        // Swap to the subscribed confirmation card after the button's
        // success dwell so the user sees the state flip cleanly.
        setTimeout(() => showSubscribedConfirmation(domainOf(lastScan.url)), 900);
      } else if (res.status === 409) {
        if (btn) { btn.disabled = false; btn.innerHTML = originalLabel; }
        if (emailInput) emailInput.disabled = false;
        if (subscribeStatusEl) subscribeStatusEl.textContent = "You're already subscribed for this store.";
      } else {
        if (btn) { btn.disabled = false; btn.innerHTML = originalLabel; }
        if (emailInput) emailInput.disabled = false;
        if (subscribeStatusEl) subscribeStatusEl.textContent = "Couldn't subscribe right now. Try again later.";
        ga4("subscribe_failed", { reason: `http_${res.status}` });
      }
    } catch {
      if (btn) { btn.disabled = false; btn.classList.remove("audit-button--loading"); btn.innerHTML = originalLabel; }
      if (emailInput) emailInput.disabled = false;
      if (subscribeStatusEl) subscribeStatusEl.textContent = "Network error. Try again later.";
    }
  });

  rateSubscribeForm?.addEventListener("submit", async (e) => {
    e.preventDefault();
    const submitBtn = rateSubscribeForm.querySelector("button[type=submit]");
    const textEl = submitBtn?.querySelector(".audit-rate-reward__submit-text");
    const setBtnState = (state, text) => {
      if (!submitBtn) return;
      submitBtn.classList.remove("audit-button--loading", "audit-button--success");
      if (state === "loading") submitBtn.classList.add("audit-button--loading");
      if (state === "success") submitBtn.classList.add("audit-button--success");
      submitBtn.disabled = state === "loading" || state === "success";
      if (textEl && typeof text === "string") textEl.textContent = text;
    };
    setBtnState("loading", "Subscribing…");

    const fd = new FormData(rateSubscribeForm);
    // Input stores the URL without the https:// prefix (shown as a locked
    // visual adornment). Re-attach before POSTing — canonicalize handles
    // stripping it if the user pasted a full URL.
    let rawUrl = String(fd.get("url") || "").trim();
    if (rawUrl && !/^https?:\/\//i.test(rawUrl)) rawUrl = "https://" + rawUrl;
    const url = rawUrl;
    const email = String(fd.get("email") || "").trim();
    try {
      const res = await fetch(`${API_BASE}/subscribe`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ url, email, depth: "standard" }),
      });
      const data = res.ok ? await res.json().catch(() => ({})) : {};
      if (res.ok && data.scan_token) {
        // Reward: subscribers get a fresh audit immediately, bypassing the
        // rate limit. Transition to the scanning state and run with the
        // one-shot token returned by /subscribe. Show a brief "Subscribed —
        // starting audit..." confirmation on the button so the flip from
        // form to scanning state doesn't feel jarring.
        ga4("audit_subscribe_reward", { depth: "standard" });
        setBtnState("success", "Subscribed — starting audit…");
        await sleep(900);
        // Pre-arm the subscribed confirmation so when the results page
        // eventually renders, the subscribe card is already swapped out.
        showSubscribedConfirmation(domainOf(url));
        lastScan = { url, depth: "standard", scanId: null, summary: null, failedKeys: [] };
        setDomain(domainOf(url));
        initCheckList({ depth: "standard" });
        go("scanning");
        await runScan({ url, depth: "standard", scan_bypass: data.scan_token });
        return;
      }
      if (res.ok) {
        // Subscribed but no token (already-active duplicate). Show success
        // on the button — no trailing status copy.
        setBtnState("success", "You're subscribed");
        return;
      }
      setBtnState(null, "Couldn't subscribe — try later");
    } catch {
      setBtnState(null, "Network error — try again");
    }
  });

  hero.addEventListener("click", (e) => {
    const t = e.target;
    // `closest` (not matches) so clicks on the inner SVG icon still hit
    // the button. matches() was silently skipping icon clicks.
    if (t?.closest?.("[data-audit-reset]")) { resetToLanding(); return; }
    if (t?.closest?.("[data-audit-rescan]")) { handleRescan(); return; }
    if (t?.closest?.("[data-audit-retry]")) { form?.requestSubmit(); return; }
  });

  // "Scan another store" — drop back to the landing state with a clean
  // slate so the next URL the user types doesn't inherit leftover scan
  // context (URL input value, subscribed-preset, failed-keys from the
  // previous store).
  function resetToLanding() {
    lastScan = { url: null, depth: null, scanId: null, summary: null, failedKeys: [] };
    subscribedPreset = false;
    if (subscribeBoxEl) subscribeBoxEl.hidden = false;
    if (subscribedBoxEl) subscribedBoxEl.hidden = true;
    if (urlInput) urlInput.value = "";
    go("landing");
    try { window.scrollTo({ top: hero.offsetTop - 20, behavior: "smooth" }); } catch {}
  }

  // "Rescan this store" — re-runs the same URL+depth with ?force=1 so
  // the API skips the cache read. Still hits Turnstile + rate limit
  // (rescan costs 1 against the 3/day budget). Reuses the same scan
  // pipeline so progress/finalize work identically.
  async function handleRescan() {
    if (!lastScan.url || !lastScan.depth) return;
    const depth = lastScan.depth;
    const url = lastScan.url;

    let token = "";
    if (!isDev) {
      token = await acquireTurnstileToken();
      if (!token) {
        showError("CAPTCHA check failed", "We couldn't verify your browser. Reload the page and try again.");
        return;
      }
    }

    // Reset scan-local state before re-entering the scanning view so the
    // progress bar + findings groups re-render cleanly.
    lastScan = { url, depth, scanId: null, summary: null, failedKeys: [] };
    setDomain(domainOf(url));
    initCheckList({ depth });
    go("scanning");
    try { window.scrollTo({ top: hero.offsetTop - 20, behavior: "smooth" }); } catch {}

    ga4("audit_rescan", { depth });
    await runScan({ url, depth, turnstile_token: token, force: true });
  }

  function showError(msg, detail) {
    errorMsgEl.textContent = msg;
    errorDetailEl.textContent = detail || "";
    go("error");
    ga4("audit_error", { error_type: msg });
  }

  function domainOf(urlStr) {
    try { return new URL(urlStr.startsWith("http") ? urlStr : `https://${urlStr}`).hostname; } catch { return urlStr; }
  }
  async function sha256Hex(s) {
    const b = new TextEncoder().encode(s);
    const d = await crypto.subtle.digest("SHA-256", b);
    return [...new Uint8Array(d)].map(x => x.toString(16).padStart(2, "0")).join("");
  }
  function rankStatus(s) { return s === "fail" ? 0 : s === "warn" ? 1 : s === "pass" ? 2 : 3; }
  function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

  // FAQ is now static — no JS needed (removed accordion per redesign feedback).

  // ── Fixture-driven UI preview ──
  // /pages/audit?state=<name>&fixture=<name> renders any state with canned
  // data, no API call. Only honored in dev bypass mode.
  const FIXTURES = {
    "weltpixel-com": {
      domain: "weltpixel.com",
      depth: "standard",
      checks: [
        { key: "platform-detect", label: "Shopify platform check", status: "pass", detail: "Confirmed Shopify storefront." },
        { key: "meta-pixel", label: "Meta Pixel", status: "pass", detail: "Meta Pixel 1234567890 detected with PageView queued." },
        { key: "tiktok-pixel", label: "TikTok Pixel", status: "pass", detail: "TikTok Pixel CRY123ABC loaded." },
        { key: "ga4-pixel", label: "Google Analytics 4 tag", status: "pass", detail: "GA4 tag G-N3FQEBMXZQ detected with page_view queued." },
        { key: "google-ads", label: "Google Ads conversion tag", status: "pass", detail: "Google Ads conversion tag AW-123456 detected." },
        { key: "consent-mode", label: "Consent Mode V2 framework", status: "pass", detail: "Consent Mode V2 initialized before tag loads." },
        { key: "ga4-events", label: "Google Analytics 4 e-commerce events", status: "pass", detail: "view_item and add_to_cart fired as expected." },
        { key: "meta-capi", label: "Meta CAPI (server-side)", status: "pass", detail: "Your store has WeltPixel Conversion Tracking installed — Meta CAPI is handled through the app. Verify Meta is enabled inside the app (Shopify admin → Apps → WeltPixel Conversion Tracking)." },
        { key: "tiktok-events-api", label: "TikTok Events API (server-side)", status: "pass", detail: "Your store has WeltPixel Conversion Tracking installed — TikTok Events API is handled through the app. Verify TikTok is enabled inside the app (Shopify admin → Apps → WeltPixel Conversion Tracking)." },
        { key: "duplicate-pixels", label: "Duplicate pixels", status: "pass", detail: "No duplicate pixel IDs detected." },
        { key: "shop-pay-gap", label: "Shop Pay checkout tracking", status: "pass", detail: "Shop Pay checkout events detected on PDP + cart flow." },
      ],
    },
    "broken-store": {
      domain: "example-broken.com",
      depth: "standard",
      checks: [
        { key: "platform-detect", label: "Shopify platform check", status: "pass", detail: "Confirmed Shopify storefront." },
        { key: "meta-pixel", label: "Meta Pixel", status: "fail", detail: "No Meta Pixel detected on the homepage." },
        { key: "tiktok-pixel", label: "TikTok Pixel", status: "fail", detail: "No TikTok Pixel detected." },
        { key: "ga4-pixel", label: "Google Analytics 4 tag", status: "warn", detail: "GA4 tag detected but no page_view event was queued during the scan." },
        { key: "google-ads", label: "Google Ads conversion tag", status: "fail", detail: "No Google Ads conversion tag detected." },
        { key: "consent-mode", label: "Consent Mode V2 framework", status: "fail", detail: "Consent Mode V2 not initialized — tags load without a consent signal." },
        { key: "ga4-events", label: "Google Analytics 4 e-commerce events", status: "fail", detail: "view_item and add_to_cart events did not fire on PDP + cart simulation." },
        { key: "meta-capi", label: "Meta CAPI (server-side)", status: "warn", detail: "We couldn't detect a server-side CAPI signal from outside your store." },
        { key: "tiktok-events-api", label: "TikTok Events API (server-side)", status: "warn", detail: "We couldn't detect a server-side TikTok Events API signal from outside your store." },
        { key: "duplicate-pixels", label: "Duplicate pixels", status: "pass", detail: "No duplicate pixel IDs detected." },
        { key: "shop-pay-gap", label: "Shop Pay checkout tracking", status: "warn", detail: "Shop Pay checkout events could not be verified from outside the checkout flow." },
      ],
    },
    "thorough-mixed": {
      domain: "store.example.com",
      depth: "thorough",
      checks: [
        { key: "platform-detect", label: "Shopify platform check", status: "pass", detail: "Confirmed Shopify storefront." },
        { key: "meta-pixel", label: "Meta Pixel", status: "pass", detail: "Meta Pixel 7777777 detected." },
        { key: "tiktok-pixel", label: "TikTok Pixel", status: "pass", detail: "TikTok Pixel CABC detected." },
        { key: "ga4-pixel", label: "Google Analytics 4 tag", status: "pass", detail: "GA4 tag G-XYZ123 detected." },
        { key: "google-ads", label: "Google Ads conversion tag", status: "warn", detail: "Google Ads detected but conversion_id not verified." },
        { key: "consent-mode", label: "Consent Mode V2 framework", status: "pass", detail: "Consent Mode V2 initialized." },
        { key: "ga4-events", label: "Google Analytics 4 e-commerce events", status: "warn", detail: "view_item fired; add_to_cart did not fire during simulation." },
        { key: "meta-capi", label: "Meta CAPI (server-side)", status: "warn", detail: "No server-side CAPI signal observed." },
        { key: "tiktok-events-api", label: "TikTok Events API (server-side)", status: "warn", detail: "No server-side TikTok Events API signal observed." },
        { key: "duplicate-pixels", label: "Duplicate pixels", status: "fail", detail: "Meta Pixel loaded twice (7777777 + 8888888). Server will dedupe — but one should be removed." },
        { key: "shop-pay-gap", label: "Shop Pay checkout tracking", status: "warn", detail: "Shop Pay checkout events not verifiable from outside." },
        { key: "consent-mode-correctness", label: "Consent Mode V2 correctness", status: "fail", detail: "analytics_storage defaults to 'granted' before the banner — violates GDPR default-deny." },
        { key: "server-gtm", label: "Server-side GTM", status: "pass", detail: "Server-side GTM container on gtm.example.com." },
        { key: "purchase-event", label: "Purchase event configuration", status: "warn", detail: "checkout_started fired; full purchase event could not be verified from outside Thank You page." },
      ],
    },
  };

  function renderFixtureState(state, fixtureName) {
    const fx = FIXTURES[fixtureName] || FIXTURES["weltpixel-com"];
    setDomain(fx.domain);
    lastScan = { url: `https://${fx.domain}`, depth: fx.depth, scanId: -1, summary: null, failedKeys: [] };

    if (state === "decline") { go("decline"); return; }
    if (state === "rate-limited") {
      go("rate-limited");
      const nextAvail = Date.now() + 35 * 60_000;
      const tick = () => {
        const ms = Math.max(0, nextAvail - Date.now());
        if (retryCountdownEl) retryCountdownEl.textContent = `${Math.floor(ms / 60000)} min ${Math.floor((ms % 60000) / 1000)} sec`;
        if (ms > 0) setTimeout(tick, 1000);
      };
      tick();
      return;
    }
    if (state === "error") {
      showError("Scan error", "Fixture: simulated engine error for UI preview.");
      return;
    }
    if (state === "scanning") {
      go("scanning");
      initCheckList({ depth: fx.depth });
      // Queue all checks to animate through the pacing queue
      fx.checks.forEach(c => enqueueCheck(c));
      return;
    }
    if (state === "results") {
      go("scanning");
      initCheckList({ depth: fx.depth });
      // Fast-forward: render synchronously without pacing, then finalize
      for (const c of fx.checks) {
        const li = currentCheckItems.get(c.key);
        if (!li) continue;
        li.dataset.status = c.status;
        const iconEl = li.querySelector(".audit-check-icon");
        if (iconEl) setIconFromStatus(iconEl, c.status);
        const labelEl = li.querySelector(".audit-check-label");
        if (labelEl) labelEl.textContent = c.label || PRETTY_LABELS[c.key] || c.key;
        currentCheckDetails.set(c.key, c.detail || "");
        const chipEl = li.querySelector(".audit-check-status-chip");
        if (chipEl) chipEl.textContent = statusChipText(c.status);
        if (c.status === "fail" || c.status === "warn") lastScan.failedKeys.push(c.key);
      }
      const summary = {
        pass: fx.checks.filter(c => c.status === "pass").length,
        fail: fx.checks.filter(c => c.status === "fail").length,
        warn: fx.checks.filter(c => c.status === "warn").length,
      };
      finalize({ scan_id: -1, summary });
      return;
    }
  }

  if (isDev) {
    const fixtureState = urlParams.get("state");
    if (fixtureState) {
      const fixtureName = urlParams.get("fixture") || "weltpixel-com";
      setTimeout(() => renderFixtureState(fixtureState, fixtureName), 0);
    }
  }
})();
